import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import LockIcon from '@material-ui/icons/Lock'

const Wrapper = styled.div`
  width: 100%;
`
const Label = styled.label<{ hasError?: boolean }>`
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #64748B;
  font-family: Rubik;
  font-style: normal;
  line-height: 16px;
  color: ${({ hasError }: { hasError?: boolean }) => (hasError ? '#d32f2f' : '#64748B')};
`
const InputWrapper = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  height: 44px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid #D1D5DC;
  background: #FFF;
  ${({ hasError }: { hasError?: boolean }) =>
    hasError &&
    css`
      border-color: #d32f2f;
      background: #fff6f6;
    `}
`
const LeftIcon = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  margin-right: 0px;
  color: ${({ hasError }: { hasError?: boolean }) => (hasError ? '#d32f2f' : '#666')};
`
const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  flex-shrink: 0;
  border: none;
  outline: none;
  font-size: 14px;
  background: ${({ hasError }: { hasError?: boolean }) => (hasError ? '#fff6f6' : '#FFF')};
  padding-left: 6px;
  min-width: 10px;

  &::placeholder {
    color: rgba(15, 23, 42, 0.50);
    font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
`
const ErrorText = styled.div`
  display: flex;
  margin-top: 4px;
  font-size: 12px;
  justify-content: flex-start;
  color: #d32f2f;
`
const StyledLabel = styled.div<{ hasError?: boolean; htmlFor?: string }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
`
const StyledRequired = styled.div`
  color: #DC2626;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
`
const LockedValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  height: 44px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
  background: #F1F5F9;
  color: #475569;
  font-size: 14px;
`
const LockGlyph = styled(LockIcon)`
  && { font-size: 15px; color: #94A3B8; }
`
const ContainerValue = styled.div`
  display: flex;
  height: 35px;
  padding: 8px 12px 8px 12px;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.30);
`

type ValidatedPopulationInputProps = {
  id: string
  label?: React.ReactNode
  value: any
  onChange: (value: string) => void
  placeholder?: string
  setValidationError?: (hasError: boolean) => void
  customValidation?: ((value: string) => boolean) | null
  disabled?: boolean
  locked?: boolean
  required?: boolean
  errorText?: React.ReactNode
  icon?: React.ReactNode
  dir?: string
}

export function ValidatedPopulationInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  // Optional: callers that do not validate (e.g. a search filter) leave it out.
  // It used to be required in practice - calling an undefined prop threw on the
  // first keystroke, so the field silently refused to accept any input.
  setValidationError = (_hasError: boolean) => {},
  customValidation = null,
  disabled = false,
  // Editable in principle, but not from here - see the population form. Unlike
  // `disabled`, which is the whole form in read-only mode and drops the frame
  // entirely, this keeps the field looking like a field and says why.
  locked = false,
  required = false,
  errorText,
  icon,
  // Only the fields that hold something written left to right pass this.
  dir,
}: ValidatedPopulationInputProps) {
  const [error, setError] = useState(false)
  return (
    <Wrapper>
      {label && (<StyledLabel htmlFor={id} hasError={error}>{label} {required && <StyledRequired>*</StyledRequired>}</StyledLabel>)}
      {disabled ? <ContainerValue>{value ?? '—'}</ContainerValue>
       : locked ? <LockedValue><LockGlyph />{value || '—'}</LockedValue>
       : <InputWrapper hasError={error}>
        <LeftIcon hasError={error}>
          {error ? <ErrorOutlineIcon fontSize="small" /> : icon}
        </LeftIcon>

        <Input
          id={id}
          hasError={error}
          // `?? ''` keeps this a controlled input. React treats value={undefined}
          // as uncontrolled and then leaves the DOM node alone, so a field the
          // contact payload simply omits kept whatever the previously loaded
          // contact had typed into it - the house number staying behind when the
          // next contact has none.
          value={value ?? ''}
          disabled={disabled}
          dir={dir}
          style={dir ? { textAlign: 'right' } : undefined}
          onChange={e => {
            if (e.target.value.trim() == '') {
              if (required) {
                setError(true)
                setValidationError(true)
              } else {
                setError(false)
                setValidationError(false)
              }
            } else if(customValidation != null) {
              const res = customValidation(e.target.value)
              if (!res) {
                setError(true)
                setValidationError(true)
              } else {
                setError(false)
                setValidationError(false)
              }
            } else {
              setError(false)
              setValidationError(false)
            }
             
            onChange(e.target.value)
          }}
          placeholder={placeholder}
        />
      </InputWrapper>}

      {error && errorText && <ErrorText>{errorText}</ErrorText>}
    </Wrapper>
  )
}

type ValidatedInputProps = {
  id: string
  label?: React.ReactNode
  value: any
  onChange: (value: string) => void
  placeholder?: string
  error?: boolean
  errorText?: React.ReactNode
  icon?: React.ReactNode
}

export function ValidatedInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  error = false,
  errorText,
  icon
}: ValidatedInputProps) {
  return (
    <Wrapper>
      {label && (
        <Label htmlFor={id} hasError={error}>
          {label}
        </Label>
      )}

      <InputWrapper hasError={error}>
        <LeftIcon hasError={error}>
          {error ? <ErrorOutlineIcon fontSize="small" /> : icon}
        </LeftIcon>

        <Input
          id={id}
          value={value ?? ''}
          hasError={error}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
      </InputWrapper>

      {error && errorText && <ErrorText>{errorText}</ErrorText>}
    </Wrapper>
  )
}
